// components/CommissionCalculator.jsx
import React, { useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/outline';
import { calculateAndStoreAllCommissions } from '../services/commissionStorageService';

const CommissionCalculator = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [tlTarget, setTlTarget] = useState(1000);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      setError(null);
      setResult(null);

      const calculationResult = await calculateAndStoreAllCommissions(year, month, tlTarget);
      setResult(calculationResult);
    } catch (err) {
      setError(err.message || 'Failed to calculate commissions');
      console.error('Calculation error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Calculate Monthly Commissions</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Year Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Year
          </label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Month Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Month
          </label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* TL Target */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Leader Target
          </label>
          <input
            type="number"
            value={tlTarget}
            onChange={(e) => setTlTarget(parseFloat(e.target.value))}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            min="0"
            step="100"
          />
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={handleCalculate}
        disabled={calculating}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PlayIcon className="h-4 w-4 mr-2" />
        {calculating ? 'Calculating...' : 'Calculate Commissions'}
      </button>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <h3 className="text-sm font-medium text-green-900 mb-2">Calculation Complete</h3>
          <div className="text-sm text-green-800 space-y-1">
            <p>Period: {result.year} - {months.find(m => m.value === result.month)?.label}</p>
            <p>Total Agents Processed: {result.totalAgentsProcessed}</p>
            <p>Total Teams Processed: {result.totalTeamsProcessed}</p>
            <p>Agents Without TL: {result.agentsWithoutTL}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionCalculator;
